import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { playPopSound } from '../utils/soundUtils';

interface CSSCodeProps {
  cssCode: string;
}

export function CSSCode({ cssCode }: CSSCodeProps) {
  const { isDarkMode } = useTheme();
  const [showCopied, setShowCopied] = useState(false);

  const handleCopyCSS = () => {
    playPopSound('medium'); // Play sound effect
    navigator.clipboard.writeText(cssCode);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  return (
    <div className={`rounded-xl p-4 ${
      isDarkMode 
        ? 'bg-black border-zinc-800' 
        : 'bg-white/50 backdrop-blur-sm border-gray-200'
    } border w-full transition-all duration-300 hover:scale-[1.01] hover:shadow-lg`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          CSS Code
        </h3>
        <button 
          onClick={handleCopyCSS}
          className={`p-2 rounded-lg text-sm flex items-center space-x-2 transition-all duration-300 ${
            isDarkMode 
              ? 'hover:bg-zinc-900 text-gray-400 hover:text-gray-200' 
              : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
          } hover:scale-105 active:scale-95`}
        >
          {showCopied ? (
            <>
              <Check size={16} className="transition-transform duration-300 group-hover:rotate-12" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy size={16} className="transition-transform duration-300 group-hover:rotate-12" />
              <span>Copy CSS</span>
            </>
          )}
        </button>
      </div>
      <div className={`w-full p-3 rounded-lg font-mono text-sm overflow-x-auto transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-zinc-900 text-gray-300 hover:bg-zinc-800' 
          : 'bg-gray-50/50 text-gray-600 hover:bg-gray-100/50'
      }`}>
        <code className="whitespace-pre-wrap break-all transition-all duration-300 hover:text-opacity-90">
          {cssCode}
        </code>
      </div>
    </div>
  );
}